import { z } from 'zod';
import '@apexdesigner/schema-tools';
import { belongsTo, options } from '@apexdesigner/schema-tools';

export const matchResult = z
  .object({
    id: z.number().hidden().describe('Unique identifier'),

    age: options([
      { value: 30, name: '30 months' },
      { value: 48, name: '48 months' },
    ]).describe('Foal age in months the result projects to.'),

    binUnder3: z
      .number()
      .nullable()
      .optional()
      .displayName('<3.0')
      .describe('Foals scoring below 3.0.'),

    bin3To4: z
      .number()
      .nullable()
      .optional()
      .displayName('3.0-3.9')
      .describe('Foals scoring 3.0 to 3.9.'),

    bin4To5: z
      .number()
      .nullable()
      .optional()
      .displayName('4.0-4.9')
      .describe('Foals scoring 4.0 to 4.9.'),

    bin5To6: z
      .number()
      .nullable()
      .optional()
      .displayName('5.0-5.9')
      .describe('Foals scoring 5.0 to 5.9.'),

    bin6To7: z
      .number()
      .nullable()
      .optional()
      .displayName('6.0-6.9')
      .describe('Foals scoring 6.0 to 6.9.'),

    bin7To8: z
      .number()
      .nullable()
      .optional()
      .displayName('7.0-7.9')
      .describe('Foals scoring 7.0 to 7.9.'),

    bin8Plus: z
      .number()
      .nullable()
      .optional()
      .displayName('8.0+')
      .describe('Foals scoring 8.0 or more.'),

    averageBme: z
      .number()
      .nullable()
      .optional()
      .displayName('Average BME')
      .describe(
        'Average BME (Biomechanical Efficiency) score of the 10,000 foals\n(legacy-exact, with the +0.5 offset).',
      ),

    standardDeviation: z
      .number()
      .nullable()
      .optional()
      .describe('Standard deviation of the BME scores.'),

    mlfScore: z
      .number()
      .nullable()
      .optional()
      .displayName('MLF Score')
      .describe(
        'BME (Biomechanical Efficiency) score of the Most Likely Foal (MLF).',
      ),

    phenotype: z
      .string()
      .nullable()
      .optional()
      .describe('Phenotype of the Most Likely Foal, e.g. "SE1", "IISP".'),

    ringOneUnder3: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring One <3.0')
      .describe('Ring one foals scoring below 3.0.'),

    ringOne3To4: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring One 3.0-3.9')
      .describe('Ring one foals scoring 3.0 to 3.9.'),

    ringOne4To5: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring One 4.0-4.9')
      .describe('Ring one foals scoring 4.0 to 4.9.'),

    ringOne5To6: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring One 5.0-5.9')
      .describe('Ring one foals scoring 5.0 to 5.9.'),

    ringOne6To7: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring One 6.0-6.9')
      .describe('Ring one foals scoring 6.0 to 6.9.'),

    ringOne7To8: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring One 7.0-7.9')
      .describe('Ring one foals scoring 7.0 to 7.9.'),

    ringOne8Plus: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring One 8.0+')
      .describe('Ring one foals scoring 8.0 or more.'),

    ringTwoUnder3: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Two <3.0')
      .describe('Ring two foals scoring below 3.0.'),

    ringTwo3To4: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Two 3.0-3.9')
      .describe('Ring two foals scoring 3.0 to 3.9.'),

    ringTwo4To5: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Two 4.0-4.9')
      .describe('Ring two foals scoring 4.0 to 4.9.'),

    ringTwo5To6: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Two 5.0-5.9')
      .describe('Ring two foals scoring 5.0 to 5.9.'),

    ringTwo6To7: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Two 6.0-6.9')
      .describe('Ring two foals scoring 6.0 to 6.9.'),

    ringTwo7To8: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Two 7.0-7.9')
      .describe('Ring two foals scoring 7.0 to 7.9.'),

    ringTwo8Plus: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Two 8.0+')
      .describe('Ring two foals scoring 8.0 or more.'),

    ringThreeUnder3: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Three <3.0')
      .describe('Ring three foals scoring below 3.0.'),

    ringThree3To4: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Three 3.0-3.9')
      .describe('Ring three foals scoring 3.0 to 3.9.'),

    ringThree4To5: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Three 4.0-4.9')
      .describe('Ring three foals scoring 4.0 to 4.9.'),

    ringThree5To6: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Three 5.0-5.9')
      .describe('Ring three foals scoring 5.0 to 5.9.'),

    ringThree6To7: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Three 6.0-6.9')
      .describe('Ring three foals scoring 6.0 to 6.9.'),

    ringThree7To8: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Three 7.0-7.9')
      .describe('Ring three foals scoring 7.0 to 7.9.'),

    ringThree8Plus: z
      .number()
      .nullable()
      .optional()
      .displayName('Ring Three 8.0+')
      .describe('Ring three foals scoring 8.0 or more.'),

    legacyId: z
      .number()
      .nullable()
      .optional()
      .disabled()
      .describe('The legacy `user_report.id`.'),

    matchId: z.number().hidden().describe('Foreign key to Match'),

    match: belongsTo('Match'),
  })
  .describe(
    "The outcome of a match at one foal age: how the 10,000 simulated foals fall\ninto the seven BME (Biomechanical Efficiency) score bins, split three ways\nby closeness to the Most Likely Foal, plus the average BME, its spread, the\nMLF score and the foal's phenotype.",
  )
  .meta({ readAs: '{match} at {age} months' })
  .as('MatchResult');

export { matchResult as matchResultSchema };
